import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table, Search } from "ui";
import FlexRow from "components/FlexRow";
import { Button, message, Popover, Space, Tooltip } from 'antd';
import { SessionTabProps } from 'components/Lectures/types';
import IncreasedStudentInterestModal from 'components/Lectures/Modals/IncreasedStudentInterestModal';
import DefaultLayout from "components/DefaultLayout";
import { SessionAttendancePrecheckResponse, useSession } from "hooks/Lectures/useSession";
import {Switch} from "../../../Form";
import ChangeStatusModal from "../../Modals/ChangeStatusModal";
import AttemptLinkingChoiceModal, { AttemptLinkingChoiceMode } from "../../Modals/AttemptLinkingChoiceModal";
import { AbsenceTag } from 'components/Tags';
import styles from './SessionWaitingList.module.scss';

const SessionWaitingList: React.FC<SessionTabProps> = ({ lecture, session, reloadSession }) => {
    const [reload, setReload] = useState(false);
    const [searchString, setSearchString] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
    const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [attemptChoiceOpen, setAttemptChoiceOpen] = useState<boolean>(false);
    const [attemptChoiceMode, setAttemptChoiceMode] = useState<AttemptLinkingChoiceMode>('mixed_statuses');
    const [attemptChoiceTotalSelected, setAttemptChoiceTotalSelected] = useState<number>(0);
    const [attemptChoiceCompletedCount, setAttemptChoiceCompletedCount] = useState<number>(0);
    const [pendingAdd, setPendingAdd] = useState<null | {
        lecturesId: number;
        sessionId: number;
        userTypeIds: number[];
    }>(null);
    const [filter, setFilter] = useState('absence=0&mandatory=0');

    const { addSessionAttendance, precheckSessionAttendance, sessionLoading } = useSession();

    const intl = useIntl();

    const capacityExceeded = (session.capacity ?? 0) < (session.attendees ?? 0) + selectedRowKeys.length;

    const handleRowActionVisibleChange = (visible: boolean, recordId: number) => {
        if (visible) {
            setVisibleTooltip(recordId);
        } else {
            setVisibleTooltip(null);
        }
    };

    type WaitingListRow = {
        id: number;
        name?: string;
        surname?: string;
        email?: string;
        position?: string;
        organizational_unit?: string;
        organization?: string;
        absence?: null | string;
    };

    const columns: Array<{
        key?: string;
        title?: string;
        dataIndex?: keyof WaitingListRow;
        render?: (_text: string, record: WaitingListRow) => React.ReactNode;
    }> = [
        {
            key: 'user',
            title: intl.formatMessage({ id: 'audience.user' }),
            render: (_text: string, record: WaitingListRow) => {
                return `${record.name ?? ''} ${record.surname ?? ''}`.trim();
            }
        },
        {
            key: 'email',
            title: intl.formatMessage({ id: 'general.email' }),
            dataIndex: 'email',
        },
        {
            title: intl.formatMessage({ id: 'general.position' }),
            dataIndex: 'position',
            key: 'position',
        },
        {
            title: intl.formatMessage({ id: 'general.organizational_unit' }),
            dataIndex: 'organizational_unit',
            key: 'organizationalUnit',
        },
        {
            title: intl.formatMessage({ id: 'general.organization' }),
            dataIndex: 'organization',
            key: 'organization',
        },
        {
            title: intl.formatMessage({ id: 'users.form.long_absence' }),
            dataIndex: 'absence',
            key: 'absence',
            render: (record: null | string) => <AbsenceTag status={!!record}/>
        },
        {
            key: 'actions',
            render: (_text: string, record: WaitingListRow) => {
                const content = !capacityExceeded ? (
                    <div className="popover-item"
                        onClick={() => handleAddAttendance(record.id)}>
                        <FormattedMessage id="general.lectures.plan_session_attendance" />
                    </div>
                ) : null;

                return (
                    <Popover
                        open={record.id === visibleTooltip}
                        content={content}
                        trigger="click"
                        placement="bottomRight"
                        arrowPointAtCenter
                        onOpenChange={(visible) => handleRowActionVisibleChange(visible, record.id)}
                    >
                        <div className={styles.actionsCell}>
                            <i className={`fal fa-ellipsis-v ${styles.actionsIcon}`} />
                        </div>
                    </Popover>
                )
            },
        },
    ];

    const getTableColmns = () => {
        if ((session.capacity ?? 0) <= (session.attendees ?? 0) + selectedRowKeys.length) {
            return columns.filter((column) => column.key !== 'actions')
        }

        return columns;
    }

    const handleAddAttendance = async (id: number | null = null): Promise<void> => {
        setVisibleTooltip(null);
        if (session && typeof session.id === 'number' && typeof session.lecturesId === 'number') {
            const userTypeIds = id ? [id] : selectedRowKeys;
            const lecturesId = session.lecturesId;
            const sessionId = session.id;

            // runAdd intentionally does NOT create new attempts by default
            const runAdd = async (createAttempt: boolean): Promise<void> => {
                if (handleAdd(await addSessionAttendance(lecturesId, sessionId, userTypeIds, createAttempt))) {
                    setReload(!reload);
                    setSelectedRowKeys([]);
                    reloadSession();
                }
            };

            const precheck = (await precheckSessionAttendance(
                lecturesId,
                sessionId,
                userTypeIds,
                false
            )) as SessionAttendancePrecheckResponse | null;
            if (!precheck) {
                return;
            }

            if (precheck.completedUserTypeIds.length === 0) {
                await runAdd(false);
                return;
            }

            const totalSelected = userTypeIds.length;
            const completedCount = precheck.completedUserTypeIds.length;

            setPendingAdd({ lecturesId, sessionId, userTypeIds });
            setAttemptChoiceTotalSelected(totalSelected);
            setAttemptChoiceCompletedCount(completedCount);

            if (completedCount !== totalSelected) {
                setAttemptChoiceMode('mixed_statuses');
                setAttemptChoiceOpen(true);
                return;
            }

            setAttemptChoiceMode('all_completed');
            setAttemptChoiceOpen(true);
        }
    }

    const handleAdd = (response: { added: number[]; skipped: number[] }) => {
        const added = response.added;
        const skipped = response.skipped;
        if (!!added.length) {
            message.success(intl.formatMessage({ id: 'lectures.users_added' }, {count: added.length}), 15);
        }

        if (!!skipped.length) {
            message.warning(intl.formatMessage({ id: 'lectures.users_skipped' }, {count: skipped.length}), 15);
        }

        if (!!skipped.length || !!added.length) {
            return true;
        }

        return false;
    }

    const handleCancelMessageModal = () => {
        setShowMessageModal(false);
    }

    const handleSentMessageModal = () => {
        setShowMessageModal(false);
        message.success(intl.formatMessage({ id: 'users.email_sent' }), 15);
    }

    const handleSwitch = (value: boolean, key: string) => {
        let tempFilter;

        if (value) {
            tempFilter = filter.replace(key + '=0', key + '=1')
        } else {
            tempFilter = filter.replace(key + '=1', key + '=0')
        }

        setFilter(tempFilter)
        setReload(true)
    }

    return (
        <>
            <FlexRow
                right={
                    <div className={'flex flex-column align-end gap-16'}>
                        <Search setSearchString={setSearchString} setReload={setReload}/>
                        <div className={'flex gap-10 strict-order'}>
                            <FormattedMessage id="lectures.long_absence"/>
                            <Switch onClick={(value: boolean) => handleSwitch(value, 'absence')}/>
                        </div>
                        <div className={'flex gap-10 strict-order'}>
                            <FormattedMessage id="lectures.mandatory_lecture"/>
                            <Switch onClick={(value: boolean) => handleSwitch(value, 'mandatory')}/>
                        </div>
                    </div>
                }
            />
            <Table
                tableKey={'sessionWaitingList'}
                columns={getTableColmns()}
                url={`/api/v1/courses/lectures/${lecture.id}/sessions/${session.id}/waiting-list`}
                reload={reload}
                setReload={setReload}
                search={searchString}
                rowKey={'id'}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                setTotalRecords={setTotalRecords}
                filter={filter}
                isLoading={sessionLoading}
            />
            <DefaultLayout.PageFooter
                left={
                    <>
                        {selectedRowKeys.length}<FormattedMessage id='users.rows_selected' />
                        <Tooltip
                            title={<div>
                                <div>{intl.formatMessage({ id: 'general.lectures.session_capacity_exceeded' }, { capacity: session.capacity ?? 0 })}</div>
                                <div className={styles.capacityTooltipButtonWrapper}>
                                    <Button
                                        onClick={() => setShowMessageModal(true)}>
                                        <FormattedMessage id='general.lectures.request_capacity_upgrade' />
                                    </Button>
                                </div>
                            </div>}
                            defaultOpen={capacityExceeded && !showMessageModal}
                            open={capacityExceeded && !showMessageModal}>
                            <Space size={5}>
                                <Button
                                    disabled={!selectedRowKeys.length || capacityExceeded}
                                    onClick={() => handleAddAttendance(null)}>
                                    <i className={`fa-solid fa-clock header-item ${styles.headerIcon}`} />
                                    <FormattedMessage id='general.lectures.plan_session_attendance' />
                                </Button>
                                <Button
                                    disabled={!selectedRowKeys.length || capacityExceeded}
                                    onClick={() => setShowStatusModal(true)}>
                                    <i className={`fa-solid fa-clock header-item ${styles.headerIcon}`} />
                                    <FormattedMessage id='general.lectures.change_status' />
                                </Button>
                            </Space>
                        </Tooltip>
                    </>
                }
            />
            {session && <IncreasedStudentInterestModal 
                visible={showMessageModal}
                session={session} 
                onCancel={handleCancelMessageModal} 
                onSubmit={handleSentMessageModal} 
            />}
            <ChangeStatusModal visible={showStatusModal} onCancel={setShowStatusModal} selectedRowKeys={selectedRowKeys}  session={session}/>
            <AttemptLinkingChoiceModal
                open={attemptChoiceOpen}
                mode={attemptChoiceMode}
                totalSelected={attemptChoiceTotalSelected}
                completedCount={attemptChoiceCompletedCount}
                onCancel={() => {
                    setAttemptChoiceOpen(false);
                    setPendingAdd(null);
                }}
                onCreateNewAttempt={async () => {
                    if (!pendingAdd) {
                        setAttemptChoiceOpen(false);
                        return;
                    }
                    setAttemptChoiceOpen(false);
                    handleAdd(await addSessionAttendance(pendingAdd.lecturesId, pendingAdd.sessionId, pendingAdd.userTypeIds, true));
                    setPendingAdd(null);
                    setReload(!reload);
                    setSelectedRowKeys([]);
                    reloadSession();
                }}
                onUseExistingAttempt={async () => {
                    if (!pendingAdd) {
                        setAttemptChoiceOpen(false);
                        return;
                    }
                    setAttemptChoiceOpen(false);
                    handleAdd(await addSessionAttendance(pendingAdd.lecturesId, pendingAdd.sessionId, pendingAdd.userTypeIds, false));
                    setPendingAdd(null);
                    setReload(!reload);
                    setSelectedRowKeys([]);
                    reloadSession();
                }}
            />
        </>
    );
};

export default SessionWaitingList;
